<template>
  <div class="ai-chat-widget">
    <!-- Drag Overlay (when chat is closed) -->
    <div v-if="isDragOver && !isOpen" class="chat-drag-overlay-closed">
      <div class="drag-drop-zone">
        <div class="drag-drop-zone-border"></div>
        <div class="drag-drop-zone-content">
          <i class="mdi mdi-file-document-plus-outline drag-drop-icon"></i>
          <span class="drag-drop-text">Drop to chat about this document</span>
        </div>
      </div>
    </div>

    <!-- Drag Overlay (when chat is open) -->
    <div v-if="isDragOver && isOpen" class="chat-drag-overlay-open">
      <div class="drag-drop-zone-border"></div>
      <div class="drag-drop-zone-content">
        <i class="mdi mdi-file-document-plus-outline drag-drop-icon"></i>
        <span class="drag-drop-text">Drop document here</span>
        <span class="drag-drop-hint">Attach to conversation</span>
      </div>
    </div>

    <!-- Toggle Button -->
    <button 
      v-if="!isOpen"
      class="chat-toggle-btn"
      :class="{ 'drag-active': isDragOver }" 
      @click="openChat"
      aria-label="Open AI Assistant"
    >
      <span class="chat-icon">💬</span>
      <span class="chat-label">AI Assistant</span>
    </button>
    
    <!-- Chat Window -->
    <div v-if="isOpen" class="chat-window" :class="{ minimized: isMinimized }">
      <!-- Header -->
      <div class="chat-header" @click="toggleMinimize" style="cursor: pointer;" title="Click to minimize/expand">
        <div class="chat-header-content">
          <button 
            v-if="!showHistory && !isMinimized"
            class="chat-header-btn" 
            @click.stop="toggleHistory"
            aria-label="Chat history"
            title="Chat History"
          >
            <i class="mdi mdi-history"></i>
          </button>
          <button 
            v-else-if="showHistory && !isMinimized"
            class="chat-header-btn" 
            @click.stop="toggleHistory"
            aria-label="Back to chat"
            title="Back to Chat"
          >
            <i class="mdi mdi-arrow-left"></i>
          </button>
          <h3 class="text-white">{{ showHistory ? 'Chat History' : (currentSessionId === 0 ? 'New Chat' : (currentSessionTitle || 'AI Assistant')) }}</h3>
        </div>
        <div class="chat-header-actions" @click.stop>
          <button 
            class="chat-header-btn" 
            @click="toggleMinimize"
            :aria-label="isMinimized ? 'Expand chat' : 'Minimize chat'"
            :title="isMinimized ? 'Expand' : 'Minimize'"
          >
            <i :class="['mdi', isMinimized ? 'mdi-chevron-up' : 'mdi-chevron-down']"></i>
          </button>
          <button 
            v-if="!showHistory && !isMinimized"
            class="chat-header-btn" 
            @click="handleNewChat"
            aria-label="New chat"
            title="New Chat"
          >
            <i class="mdi mdi-plus"></i>
          </button>
          <button 
            class="chat-close-btn" 
            @click="closeChat"
            aria-label="Close chat"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Session History Panel -->
      <div v-if="showHistory && !isMinimized" class="chat-history-panel">
        <div v-if="sessionsLoading" class="chat-history-loading">
          <div class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>Loading conversations...</p>
        </div>
        <div v-else-if="sessions.length === 0" class="chat-history-empty">
          <i class="mdi mdi-chat-outline"></i>
          <p>No conversations yet</p>
          <button class="btn-new-chat" @click="handleNewChat">
            <i class="mdi mdi-plus"></i> Start a new chat
          </button>
        </div>
        <div v-else class="chat-history-list">
          <div 
            v-for="session in sessions" 
            :key="session.id" 
            :class="['chat-history-item', { 'active': currentSessionId === session.id }]"
            @click="loadSession(session)"
          >
            <div class="history-item-icon">
              <i class="mdi mdi-chat-processing-outline"></i>
            </div>
            <div class="history-item-content">
              <div class="history-item-title">{{ session.title || 'Untitled Chat' }}</div>
              <div class="history-item-date">{{ formatSessionDate(session.startedAt) }}</div>
            </div>
            <button class="history-item-remove" @click="onDeleteSession(session.id)" title="Remove conversation" @click.stop>
              <i class="mdi mdi-close"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Messages Area -->
      <div v-show="!showHistory && !isMinimized" class="chat-messages" ref="messagesContainer">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="chat-welcome">
          <p>👋 Hello! How can I help you today?</p>
          <p class="chat-hint">Ask me anything about your documents in the system.</p>
        </div>
        
        <!-- Messages List -->
        <div 
          v-for="message in messages" 
          :key="message.id" 
          :class="['chat-message', message.role]"
        >
          <div class="message-content">
            <div v-if="message.role === 'ai'" class="markdown-body" v-html="renderMarkdown(message.text)"></div>
            <p v-else>{{ message.text }}</p>
            <div v-if="message.citations && message.citations.length > 0" class="message-citations">
              <small class="citations-label">📎 Sources ({{ message.citations.length }})</small>
              <div 
                v-for="(citation, index) in message.citations" 
                :key="index" 
                class="citation-attachment"
                @click="handleCitationClick(citation)"
                role="button"
                tabindex="0"
                :title="citation.Snippet || ''"
              >
                <div class="attachment-content">
                  <div class="attachment-top-row">
                    <div class="attachment-icon">
                      <i :class="getFileIcon(citation.FileType || citation.FileName)"></i>
                    </div>
                    <div class="attachment-info">
                      <div class="attachment-name" :title="citation.FileManagerName">{{ formatFileName(citation.FileManagerName) }}</div>
                      <div v-if="citation.FileManagerDescription" class="attachment-page">{{ citation.FileManagerDescription }}</div>
                    </div>
                    <div class="attachment-action">
                      <i class="mdi mdi-open-in-new"></i>
                    </div>
                  </div>
                  <div v-if="citation.FolderLocation" class="attachment-path" :title="citation.FolderLocation">
                    <i class="mdi mdi-folder-outline"></i> {{ citation.FolderLocation }}
                  </div>
                  <div v-if="citation.Location" class="attachment-path" :title="citation.Location">
                    <i class="mdi mdi-map-marker-outline"></i> {{ citation.Location }}
                  </div>
                </div>
              </div>
            </div>
            <span v-if="!message.isTyping" class="message-time">
              {{ formatTime(message.timestamp) }}
            </span>
          </div>
        </div>
        
        <!-- Typing Indicator -->
        <div v-if="isLoading" class="chat-message ai">
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Attached Document Banner -->
      <div v-if="!showHistory && !isMinimized && fileData" class="chat-attached-banner">
        <div class="attached-banner-content">
          <i :class="getFileIcon(fileData.fileName)"></i>
          <div class="attached-banner-info">
            <span class="attached-banner-name" :title="fileData.fileName">{{ fileData.fileName }}</span>
            <span class="attached-banner-meta" v-if="fileData.size">
              {{ fileData.size ? formatFileSize(fileData.size) : '' }}
            </span>
          </div>
        </div>
        <button class="attached-banner-remove" @click="detachDocument" title="Remove attached document">
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <!-- <div v-if="!showHistory && documentData" class="chat-attached-banner">
        <div class="attached-banner-content">
          <i class="mdi mdi-file-document-outline"></i>
          <div class="attached-banner-info">
            <span class="attached-banner-name" :title="documentData.name">{{ documentData.name }}</span>
            <span class="attached-banner-meta" v-if="documentData.documentCategory">
              {{ documentData.documentCategory.category }}
              <span v-if="documentFiles.length"> &middot; {{ documentFiles.length }} file{{ documentFiles.length > 1 ? 's' : '' }}</span>
            </span>
          </div>
        </div>
        <button class="attached-banner-remove" @click="detachDocument" title="Remove attached document">
          <i class="mdi mdi-close"></i>
        </button>
      </div> -->

      <!-- Attached Files Panel -->
      <!-- <div v-if="!showHistory && documentData && documentFiles.length > 0" class="chat-attached-files">
        <div class="attached-files-header" @click="showAttachedFiles = !showAttachedFiles">
          <div class="attached-files-label">
            <i class="mdi mdi-paperclip"></i>
            <span>Attached Files ({{ documentFiles.length }})</span>
          </div>
          <i :class="['mdi', showAttachedFiles ? 'mdi-chevron-down' : 'mdi-chevron-up']" class="attached-files-toggle"></i>
        </div>
        <div v-if="showAttachedFiles" class="attached-files-list">
          <div 
            v-for="file in documentFiles" 
            :key="file.id" 
            class="attached-file-item"
            :title="file.fileName"
          >
            <div class="attached-file-icon">
              <i :class="getFileIcon(file.fileName)"></i>
            </div>
            <div class="attached-file-content">
              <div class="attached-file-name">{{ formatFileName(file.fileName) }}</div>
              <div class="attached-file-meta">
                <span class="attached-file-size">{{ formatFileSize(file.size) }}</span>
                <span class="attached-file-date">{{ formatSessionDate(file.dateTimeUploaded) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div> -->

      <!-- Input Form -->
      <form v-show="!showHistory && !isMinimized" class="chat-input-form" @submit.prevent="submit">
        <input
          type="text"
          class="chat-input"
          :placeholder="fileData ? 'Ask about this file...' : 'Type your message...'"
          v-model="inputValue"
          :disabled="isLoading"
          ref="chatInput"
        />
        <button 
          type="submit" 
          class="chat-send-btn"
          :disabled="!inputValue.trim() || isLoading"
          aria-label="Send message"
        >
          ➤
        </button>
      </form>
    </div>
    
    <!-- File Viewer Modal -->
    <SingleFileViewerModal 
      :isOpen="fileViewerOpen"
      :citation="selectedCitation"
      @close="closeFileViewer"
    />
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import { marked } from 'marked';
import SingleFileViewerModal from './single-file-viewer-modal.vue';

export default {
  name: 'AIChatWidget',
  
  components: {
    SingleFileViewerModal
  },
    
  data() {
    return {
      isOpen: false,
      messages: [],
      inputValue: '',
      isLoading: false,
      currentStreamingMessageId: null,
      typingQueue: [],
      isTyping: false,
      fileViewerOpen: false,
      selectedCitation: null,
      showHistory: false,
      showAttachedFiles: false,
      isDragOver: false,
      isMinimized: false,
    };
  },
  
  computed: {
    ...mapState('aiChat/chat', {
      sessions: state => state.sessions,
      sessionsLoading: state => state.sessionsLoading,
      currentSessionId: state => state.currentSessionId,
      sessionMessagesLoading: state => state.sessionMessagesLoading,
      currentSessionTitle: state => state.currentSessionTitle,
      documentData: state => state.documentData,
      fileData: state => state.fileData,
      documentDataLoading: state => state.documentDataLoading,
    }),
    documentFiles() {
      return (this.documentData && this.documentData.fileManagerFiles) || [];
    },
    attachedDocumentId() {
      return this.documentData ? this.documentData.id : null;
    },
    attachedFileId() {
      return this.fileData ? this.fileData.id : null;
    },
  },
  
  watch: {
    messages() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    isOpen(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      }
    },
    // Auto-open chat in a new session when a file is attached
    fileData(newVal, oldVal) {
      if (newVal && (!oldVal || newVal.id !== oldVal.id)) {
        this.isOpen = true;
        this.isMinimized = false;
        this.showHistory = false;
        this.showAttachedFiles = false;
        this.startNewSession();
        this.messages = [];
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      }
    }
  },
  
  methods: {
    ...mapActions({
        sendChatMessage: 'aiChat/chat/sendChatMessage',
        fetchChatSessions: 'aiChat/chat/fetchChatSessions',
        fetchSessionMessages: 'aiChat/chat/fetchSessionMessages',
        fetchSessionTitle: 'aiChat/chat/fetchSessionTitle',
        startNewSession: 'aiChat/chat/startNewSession',
        setCurrentSession: 'aiChat/chat/setCurrentSession',
        fetchDocumentById: 'aiChat/chat/fetchDocumentById',
        setDocumentData: 'aiChat/chat/setDocumentData',
        clearDocumentData: 'aiChat/chat/clearDocumentData',
        deleteSession: 'aiChat/chat/deleteSession',
    }),
    ...mapMutations({
        setCurrentSessionId: 'aiChat/chat/setCurrentSessionId',
        setCurrentSessionTitle: 'aiChat/chat/setCurrentSessionTitle',
    }),

    openChat() {
      this.isOpen = true;
      this.isMinimized = false;
    },
    
    closeChat() {
      this.isOpen = false;
      this.isMinimized = false;
    },

    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
    },
    
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },

    formatSessionDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    },

    toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory) {
        this.fetchChatSessions().catch(() => {});
      }
    },

    handleNewChat() {
      this.startNewSession();
      this.detachDocument();
      this.messages = [];
      this.showHistory = false;
      this.$nextTick(() => {
        this.$refs.chatInput?.focus();
      });
    },

    async loadSession(session) {
      this.setCurrentSessionId(session.id);
      this.setCurrentSessionTitle(session.title || 'AI Assistant');
      this.showHistory = false;
      this.messages = [];
      this.isLoading = true;

      try {
        const serverMessages = await this.fetchSessionMessages(session.id);
        this.messages = (serverMessages || []).map((msg, idx) => {
          // Normalize role - "User" or "Assistant" from API
          const roleLower = (msg.role || '').toLowerCase();
          const isUser = roleLower === 'user' || roleLower === 'human';

          // Parse citationsJson string if present
          let citations = [];
          if (msg.citationsJson) {
            try {
              citations = JSON.parse(msg.citationsJson);
            } catch (e) {
              console.error('Error parsing citationsJson:', e);
            }
          }
          
          return {
            id: `session-${session.id}-${idx}`,
            role: isUser ? 'user' : 'ai',
            text: msg.content || '',
            timestamp: msg.createdAt || new Date(),
            citations,
            isTyping: false,
          };
        });
      } catch (error) {
        console.error('Error loading session messages:', error);
        this.messages = [];
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
          this.$refs.chatInput?.focus();
        });
      }
    },

    async submit() {
      await this.handleSendMessage();
    },
    
    async handleSendMessage() {
      if (!this.inputValue.trim() || this.isLoading) return;

      const userMessage = {
        id: Date.now().toString(),
        role: 'user',
        text: this.inputValue.trim(),
        timestamp: new Date(),
      };

      this.messages.push(userMessage);
      const messageText = this.inputValue.trim();
      this.inputValue = '';
      this.isLoading = true;

      // Track if this is a new session so we can fetch session data after
      const wasNewSession = this.currentSessionId === 0;

      // Create AI message placeholder
      const aiMessageId = (Date.now() + 1).toString();
      const aiMessage = {
        id: aiMessageId,
        role: 'ai',
        text: '',
        timestamp: new Date(),
        citations: [],
        isTyping: true
      };
      
      this.messages.push(aiMessage);
      this.currentStreamingMessageId = aiMessageId;

      try {
        await this.sendChatMessage({
          text: messageText,
          documentId: this.attachedFileId,
          onChunk: (data) => {
            if (data.type === 'message') {
              this.typingQueue.push({ messageId: aiMessageId, text: data.data, type: 'message' });
              this.processTypingQueue();
            } else if (data.type === 'citations') {
              this.typingQueue.push({ messageId: aiMessageId, citations: data.data, type: 'citations' });
            }

            if(data.sessionId) {
              this.setCurrentSessionId(data.sessionId);
            }
            // Session ID is already committed in the store action via SSE event
          }
        });

      } catch (error) {
        console.error('Error streaming AI response:', error);
        
        const messageIndex = this.messages.findIndex(m => m.id === aiMessageId);
        if (messageIndex !== -1) {
          this.messages[messageIndex].text = 'Sorry, I encountered an error. Please try again.';
        }
      } finally {
        this.isLoading = false;
        this.currentStreamingMessageId = null;
        
        const messageIndex = this.messages.findIndex(m => m.id === aiMessageId);
        if (messageIndex !== -1) {
          this.messages[messageIndex].isTyping = false;
          this.$set(this.messages, messageIndex, { ...this.messages[messageIndex] });
        }
        
        // After first message in a new session, fetch the session title once typing animation is done
        if (wasNewSession && this.currentSessionId && this.currentSessionId !== 0) {
          this.waitForTypingToComplete().then(async () => {
            try {
              const title = await this.fetchSessionTitle(this.currentSessionId);
              await this.typeOutTitle(title);
              this.setCurrentSession({
                id: this.currentSessionId,
                title,
                startedAt: new Date().toISOString()
              });
            } catch (e) {
              console.error('Error fetching session title:', e);
            }
          });
        }
        
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      }
    },
    
    waitForTypingToComplete() {
      return new Promise((resolve) => {
        const checkTyping = () => {
          if (!this.isTyping && this.typingQueue.length === 0) {
            resolve();
          } else {
            setTimeout(checkTyping, 100);
          }
        };
        checkTyping();
      });
    },

    async typeOutTitle(fullTitle) {
      if (!fullTitle) return;
      
      let currentText = '';
      const chars = fullTitle.split('');
      
      for (const char of chars) {
        currentText += char;
        this.setCurrentSessionTitle(currentText);
        
        const delay = 80 + Math.random() * 70;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    },
    
    async processTypingQueue() {
      if (this.isTyping || this.typingQueue.length === 0) return;
      
      this.isTyping = true;
      
      while (this.typingQueue.length > 0) {
        const item = this.typingQueue.shift();
        const messageIndex = this.messages.findIndex(m => m.id === item.messageId);
        
        if (messageIndex === -1) continue;
        
        if (item.type === 'message') {
          const chars = item.text.split('');
          for (const char of chars) {
            this.messages[messageIndex].text += char;
            this.$set(this.messages, messageIndex, { ...this.messages[messageIndex] });
            
            const delay = 20 + Math.random() * 40;
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        } else if (item.type === 'citations') {
          this.messages[messageIndex].citations = item.citations;
          this.$set(this.messages, messageIndex, { ...this.messages[messageIndex] });
        }
      }
      
      if (this.typingQueue.length === 0 && this.messages.length > 0) {
        const lastMessage = this.messages[this.messages.length - 1];
        if (lastMessage.role === 'ai' && lastMessage.isTyping) {
          lastMessage.isTyping = false;
          this.$set(this.messages, this.messages.length - 1, { ...lastMessage });
        }
      }
      
      this.isTyping = false;
    },
    
    
    clearChat() {
      this.messages = [];
    },
    
    handleCitationClick(citation) {
      this.selectedCitation = citation;
      this.fileViewerOpen = true;
    },
    
    closeFileViewer() {
      this.fileViewerOpen = false;
      this.selectedCitation = null;
    },
    
    getFileIcon(fileTypeOrName) {
      const fileName = fileTypeOrName || '';
      const extension = fileName.includes('.') 
        ? fileName.split('.').pop().toLowerCase()
        : fileName.toLowerCase();
      
      const iconMap = {
        'pdf': 'mdi mdi-file-pdf-box text-danger',
        'doc': 'mdi mdi-file-word-box text-primary',
        'docx': 'mdi mdi-file-word-box text-primary',
        'xls': 'mdi mdi-file-excel-box text-success',
        'xlsx': 'mdi mdi-file-excel-box text-success',
        'csv': 'mdi mdi-file-delimited text-success',
        'ppt': 'mdi mdi-file-powerpoint-box text-warning',
        'pptx': 'mdi mdi-file-powerpoint-box text-warning',
        'jpg': 'mdi mdi-file-image text-info',
        'jpeg': 'mdi mdi-file-image text-info',
        'png': 'mdi mdi-file-image text-info',
        'gif': 'mdi mdi-file-image text-info',
        'bmp': 'mdi mdi-file-image text-info',
        'svg': 'mdi mdi-file-image text-info',
        'txt': 'mdi mdi-file-document-outline',
        'zip': 'mdi mdi-folder-zip text-secondary',
        'rar': 'mdi mdi-folder-zip text-secondary',
        '7z': 'mdi mdi-folder-zip text-secondary',
        'default': 'mdi mdi-file-document'
      };
      
      return iconMap[extension] || iconMap['default'];
    },
    
    formatFileName(fileName) {
      if (!fileName) return '';
      
      const maxLength = 35;
      if (fileName.length <= maxLength) {
        return fileName;
      }
      
      const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      
      if (extension) {
        const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);
        return `${truncatedName}...${extension}`;
      }
      
      return fileName.substring(0, maxLength) + '...';
    },
    
    formatSnippet(snippet) {
      if (!snippet) return '';
      const maxLength = 80;
      if (snippet.length <= maxLength) {
        return snippet;
      }
      return snippet.substring(0, maxLength) + '...';
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    },

    detachDocument() {
      this.clearDocumentData();
      this.showAttachedFiles = false;
    },

    onDragOver(event) {
      // Check if drag data contains a fileItem (from file manager)
      if (event.dataTransfer.types.includes('fileitem')) {
        this.isDragOver = true;
        event.dataTransfer.dropEffect = 'move';
      }
    },

    onDragLeave(event) {
      // Only clear if leaving the widget entirely
      const rect = this.$el.getBoundingClientRect();
      const { clientX, clientY } = event;
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        this.isDragOver = false;
      }
    },

    onDrop(event) {
      this.isDragOver = false;
      const fileItemData = event.dataTransfer.getData('fileItem');
      if (fileItemData) {
        try {
          const item = JSON.parse(fileItemData);
          if (item && item.id) {
            // If full document data is in drag, use it directly
            if (item.fileManagerFiles) {
              this.setDocumentData(item);
            } else {
              // Fetch full document data by ID
              this.fetchDocumentById(item.id).catch(() => {});
            }

            // Open chat and start new session
            this.isOpen = true;
            this.showHistory = false;
            this.handleNewChat();
            this.$nextTick(() => {
              this.$refs.chatInput?.focus();
            });
          }
        } catch (e) {
          console.error('Error parsing dropped file data:', e);
        }
      }
    },

    renderMarkdown(text) {
      if (!text) return '';
      try {
        // Insert newline before numbered list patterns (e.g. "text1. item")
        // Use lookahead (?=\s) to avoid breaking decimal/currency numbers like ₱24,999,660.00
        let formattedText = text.replace(/(\S)(\d+\.)(?=\s)/g, '$1\n$2');
        return marked(formattedText, {
          breaks: true,
          gfm: true,
        });
      } catch (e) {
        return text;
      }
    },

    async onDeleteSession(sessionId) {
      this.$deleteModal('session', 'remove')
        .then( async (result) => {
          
          if (result.value) {
            
            await this.deleteSession(sessionId)
              .then(() => {
                this.$toast.success('Conversation is successfully deleted');
              })
              .catch(() => {
                this.$toast.error('Delete conversation error');
              });
            
          }
          
        });
    }
  }
};
</script>

<style scoped>
@import './ai-chat-widget.css';
</style>